import { Button, ButtonVariants, InputLabel, Modal, SelectInput } from '@triumph/design-system';
import { startCase } from 'es-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useController, useFormContext } from 'react-hook-form';

import { LinkifyTextarea } from '@/components/shared/components/Linkify/LinkifyTextarea';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { useSubmitApplicationStatusMutation } from '@/components/shared/api/mutations/applicant';
import { ClampedText } from '@/components/shared/components/ClampedText';
import { Linkify } from '@/components/shared/components/Linkify';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import { DecisionDetailStatuses } from '../../Applicant.types';
import { AddConditionForm } from '../ApplicantConditions/AddConditionForm';
import { WarningPill } from '../ApplicantConditions/ApplicantConditions.StyledComponents';
import { Condition } from '../ApplicantConditions/ApplicantConditions.types';
import {
    formatDateShort,
    getProductScopeLabel,
} from '../ApplicantConditions/ApplicantConditions.utils';
import {
    AddConditionButtonRow,
    CompactFieldLabel,
    CompactFieldValue,
    ConditionsWrapper,
    DecisionErrorBanner,
    DenialFieldsWrapper,
    DropdownWrapper,
    ModalContainer,
    OpenConditionBody,
    OpenConditionDescription,
    OpenConditionMeta,
    OpenConditionProductPill,
    OpenConditionRow,
    OpenConditionsTitle,
    OpenConditionsWrapper,
    SectionDivider,
    StatusSelectWrapper,
} from './ApplicantStatus.StyledComponents';

const DENIAL_REASONS = [
    'CIP Mismatch',
    'Customer Withdrew application',
    'Denied - Other',
    'Duplicate Application',
    'Expired Application +30 Days',
    'Fraud Score',
    'Fraud Risk',
    'Secondary Review Failure',
    'SOS Mismatch',
    'Test',
].map((r) => ({ label: r, value: r }));

const allowedStatuses = [DecisionDetailStatuses.APPROVED, DecisionDetailStatuses.DENIED];

interface DecisionModalForm {
    status: string;
    reason: string;
    notes: string;
}

export const ProductDecisionModal = ({
    applicantId,
    product,
    showModal,
    setShowModal,
    conditions = [],
}: {
    applicantId: string;
    product: ProductNames;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    conditions?: Condition[];
}) => {
    const { applicantStatus, applicantLoadPayStatus, applicantProductNames } =
        useApplicantContext();
    const [isDecisionLoading, setIsDecisionLoading] = useState(false);
    const [isAddingCondition, setIsAddingCondition] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const conditionsRef = useRef<HTMLDivElement | null>(null);

    // When the inline add form expands, scroll the modal down so the Cancel /
    // Save Condition buttons come into view.
    useEffect(() => {
        if (!isAddingCondition) return;
        const footer = conditionsRef.current
            ?.closest('[data-testid="model-container"]')
            ?.querySelector('[data-testid="modal-footer"]');
        footer?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
    }, [isAddingCondition]);

    const { isBlocksApprovalEnforcementEnabled } = useFeatureFlags();

    const openConditions = useMemo(
        () =>
            conditions.filter((c) => !c.resolved && !c.isDeleted && c.products?.includes(product)),
        [conditions, product],
    );

    const approvalBlockingConditions = useMemo(
        () =>
            isBlocksApprovalEnforcementEnabled
                ? conditions.filter(
                      (c) => !c.resolved && !c.isDeleted && c.blocksApproval?.includes(product),
                  )
                : [],
        [conditions, product, isBlocksApprovalEnforcementEnabled],
    );

    const isApprovalBlocked = approvalBlockingConditions.length > 0;

    const isLoadPay = useMemo(() => product === ProductNames.LOADPAY, [product]);

    const currentStatus = useMemo(
        () => (isLoadPay ? applicantLoadPayStatus : applicantStatus),
        [isLoadPay, applicantStatus, applicantLoadPayStatus],
    );

    const queryClient = useQueryClient();
    const { mutateAsync: submitApplicationStatus } = useSubmitApplicationStatusMutation();

    const {
        control,
        register,
        reset,
        watch,
        handleSubmit,
        formState: { errors },
    } = useFormContext<DecisionModalForm>();

    const {
        field: { value: notesValue, onChange: onNotesChange },
    } = useController({ control, name: 'notes', defaultValue: '' });

    const isDenied = watch('status') === DecisionDetailStatuses.DENIED;

    const dropdownOptions = useMemo(() => {
        return allowedStatuses
            .filter((status) => !(isApprovalBlocked && status === DecisionDetailStatuses.APPROVED))
            .map((status) => ({
                value: status,
                label: startCase(status),
            }));
    }, [isApprovalBlocked]);

    const handleModalClose = () => {
        setShowModal(false);
        setIsAddingCondition(false);
        setSubmitError(null);
        reset();
    };

    const onSubmit: SubmitHandler<DecisionModalForm> = async (data) => {
        if (applicantId) {
            setIsDecisionLoading(true);
            setSubmitError(null);
            try {
                const status = data.status as DecisionDetailStatuses;
                const isDeniedStatus = status === DecisionDetailStatuses.DENIED;

                await submitApplicationStatus({
                    applicantId,
                    product,
                    status,
                    ...(isDeniedStatus && {
                        reason: data.reason,
                        notes: data.notes || undefined,
                    }),
                });

                // The decision adds a status note to activity, and a LoadPay one can attach open conditions.
                await Promise.all(
                    ['applicant', 'applicant-activity', 'applicant-conditions'].map((prefix) =>
                        queryClient.invalidateQueries({ queryKey: [prefix, applicantId] }),
                    ),
                );
                handleModalClose();
            } catch (err) {
                // The server rejects an approval blocked by an open condition with 422.
                const status = (err as { response?: { status?: number } })?.response?.status;
                setSubmitError(
                    status === 422
                        ? 'Approval is blocked by an open task/condition for this product.'
                        : 'Something went wrong submitting this decision. Please try again.',
                );
                console.error(`Error: ${err}`);
            } finally {
                setIsDecisionLoading(false);
            }
        }
    };

    return (
        <Modal
            bodyContent={
                <ModalContainer>
                    <DropdownWrapper>
                        <CompactFieldLabel>Current Status:</CompactFieldLabel>
                        <CompactFieldValue>{currentStatus}</CompactFieldValue>
                    </DropdownWrapper>
                    {submitError ? (
                        <DecisionErrorBanner data-testid="decision-submit-error">
                            {submitError}
                        </DecisionErrorBanner>
                    ) : null}
                    <StatusSelectWrapper>
                        <SelectInput
                            {...register('status')}
                            data-testid="product-status-dropdown"
                            options={dropdownOptions}
                            label={<CompactFieldLabel as="span">Status Decision</CompactFieldLabel>}
                            errorMsg={errors?.status?.message}
                        />
                    </StatusSelectWrapper>
                    {isDenied ? (
                        <DenialFieldsWrapper>
                            <div>
                                <SelectInput
                                    {...register('reason')}
                                    data-testid="denial-reason-dropdown"
                                    options={DENIAL_REASONS}
                                    label="Reason"
                                    errorMsg={errors?.reason?.message}
                                />
                            </div>
                            <div>
                                <InputLabel htmlFor="decision-notes">Notes</InputLabel>
                                <LinkifyTextarea
                                    id="decision-notes"
                                    value={notesValue ?? ''}
                                    onChange={onNotesChange}
                                    spellCheck
                                />
                            </div>
                        </DenialFieldsWrapper>
                    ) : (
                        <ConditionsWrapper ref={conditionsRef}>
                            {openConditions.length > 0 ? <SectionDivider /> : null}
                            {openConditions.length > 0 ? (
                                <OpenConditionsWrapper data-testid="decision-open-conditions">
                                    <OpenConditionsTitle>
                                        Open Tasks/Conditions ({openConditions.length})
                                    </OpenConditionsTitle>
                                    {openConditions.map((condition) => {
                                        const scope = getProductScopeLabel(condition.products);
                                        const blocksScope = isBlocksApprovalEnforcementEnabled
                                            ? getProductScopeLabel(condition.blocksApproval)
                                            : '';
                                        return (
                                            <OpenConditionRow key={condition.id}>
                                                <OpenConditionBody>
                                                    <OpenConditionDescription>
                                                        <ClampedText>
                                                            <Linkify>
                                                                {condition.description}
                                                            </Linkify>
                                                        </ClampedText>
                                                    </OpenConditionDescription>
                                                    <OpenConditionMeta>
                                                        {scope ? (
                                                            <OpenConditionProductPill>
                                                                {scope}
                                                            </OpenConditionProductPill>
                                                        ) : null}
                                                        {blocksScope ? (
                                                            <WarningPill
                                                                variant="amber"
                                                                data-testid={`decision-blocks-approval-${condition.id}`}
                                                            >
                                                                ⚠ Blocks approval for {blocksScope}
                                                            </WarningPill>
                                                        ) : null}
                                                        <span>
                                                            {condition.createdBy} ·{' '}
                                                            {formatDateShort(condition.dateCreated)}
                                                        </span>
                                                    </OpenConditionMeta>
                                                </OpenConditionBody>
                                            </OpenConditionRow>
                                        );
                                    })}
                                </OpenConditionsWrapper>
                            ) : null}
                            {openConditions.length > 0 ? <SectionDivider /> : null}
                            {isAddingCondition ? (
                                <AddConditionForm
                                    applicantId={applicantId}
                                    availableProducts={applicantProductNames}
                                    defaultProducts={[product]}
                                    descriptionLabel="Add Task/Condition"
                                    saveLabel="Save Task/Condition"
                                    cancelLabel="Cancel Condition"
                                    actionsAlign="left"
                                    onAdded={() => setIsAddingCondition(false)}
                                    onCancel={() => setIsAddingCondition(false)}
                                />
                            ) : (
                                <AddConditionButtonRow>
                                    <Button
                                        variant={ButtonVariants.primary}
                                        onClick={() => setIsAddingCondition(true)}
                                        data-testid="decision-add-condition"
                                    >
                                        Add Task/Condition
                                    </Button>
                                </AddConditionButtonRow>
                            )}
                        </ConditionsWrapper>
                    )}
                </ModalContainer>
            }
            headerContent={`Make a Decision for ${isLoadPay ? 'LoadPay' : 'Factoring'}`}
            id="product-decision-modal"
            modalMaxHeight="90vh"
            onClose={handleModalClose}
            onSecondaryAction={handleModalClose}
            onPrimaryAction={() => {
                // onSubmit reports its own errors in the banner, so nothing reaches this catch.
                handleSubmit(onSubmit)().catch(() => {});
            }}
            primaryAdditionalProps={{
                loading: isDecisionLoading,
                disabled: isDecisionLoading,
            }}
            visible={showModal}
            primaryButtonText="Submit"
            secondaryButtonText="Cancel"
        />
    );
};
